/** New activity dialog: pick a tenant + name + type (+ optional category), POST
 *  (plain JSON), then navigate to the editor for the rest of the fields. */
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import * as activitiesApi from '@/api/activities';
import { ACTIVITY_TYPE_LABELS, type ActivityType } from '@/api/activities';
import * as categoriesApi from '@/api/activityCategories';
import type { ActivityCategoryRow } from '@/api/activityCategories';
import { ConsoleApiError } from '@/api/client';
import { RemoteAutocomplete } from '@/components/RemoteAutocomplete/RemoteAutocomplete';
import { TenantPicker, type TenantPickerValue } from '@/components/TenantPicker/TenantPicker';
import { useSnackbar } from '@/components/Snackbar';
import { useCreateDialogTenant } from '@/scope/useCreateDialogTenant';

import { catalogWriteErrors } from '@/api/catalogWriteErrors';

const ACTIVITY_TYPES: ActivityType[] = [1, 2, 3, 4, 5];

export function ActivityCreateDialog({
  open,
  onClose,
  pageTenants,
}: {
  open: boolean;
  onClose: () => void;
  /** The page's operators (D9) — see `useCreateDialogTenant`. */
  pageTenants?: TenantPickerValue[];
}) {
  const navigate = useNavigate();
  const snackbar = useSnackbar();
  const qc = useQueryClient();
  const [tenant, setTenant, resetTenant] = useCreateDialogTenant(open, pageTenants);
  const [name, setName] = useState('');
  const [type, setType] = useState<ActivityType>(1);
  const [category, setCategory] = useState<ActivityCategoryRow | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    resetTenant();
    setName('');
    setType(1);
    setCategory(null);
    setError(null);
  };

  // Page tenants: every open starts a fresh form on the page's tenant, so a
  // Cancelled pick (and the fields that hang on it) never carries over.
  useEffect(() => {
    if (open && pageTenants !== undefined) reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const mutation = useMutation({
    mutationFn: () =>
      activitiesApi.create({
        tour_operator: tenant!.id,
        name: name.trim(),
        type,
        ...(category ? { category: category.id } : {}),
      }),
    onSuccess: (activity) => {
      snackbar.show('Activity created.', 'success');
      qc.invalidateQueries({ queryKey: ['activities'] });
      reset();
      onClose();
      navigate(`/activities/${activity.id}`);
    },
    onError: (e) => {
      // The name the server refused, else the request-level `detail` (the
      // message) — never `detail` / `code` read as fields.
      const nameErr = catalogWriteErrors(e)?.fields.name;
      setError(nameErr ?? (e instanceof ConsoleApiError ? e.message : 'Create failed.'));
    },
  });

  const canSubmit = tenant !== null && name.trim().length > 0 && !mutation.isPending;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>New activity</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TenantPicker
            value={tenant}
            featured={pageTenants}
            onChange={(t) => { setTenant(t); setCategory(null); }}
            label="Operator" placeholder="Select operator"
          />
          <TextField
            label="Name" size="small" fullWidth value={name} required
            onChange={(e) => setName(e.target.value)} inputProps={{ maxLength: 1024 }}
          />
          <TextField
            select label="Type" size="small" fullWidth value={type}
            onChange={(e) => setType(Number(e.target.value) as ActivityType)}
          >
            {ACTIVITY_TYPES.map((t) => (
              <MenuItem key={t} value={t}>{ACTIVITY_TYPE_LABELS[t]}</MenuItem>
            ))}
          </TextField>
          <RemoteAutocomplete<ActivityCategoryRow>
            label="Category (optional)" size="small" value={category}
            getOptionLabel={(o) => o.title} getOptionKey={(o) => o.id}
            fetchOnEmpty={tenant !== null}
            queryFn={async (q) =>
              tenant
                ? (await categoriesApi.list({ tour_operator_id: tenant.id, search: q || undefined, pageSize: 50 })).results
                : []
            }
            onChange={(v) => setCategory((v as ActivityCategoryRow | null) ?? null)}
          />
          {error && <Box sx={{ color: 'error.main', fontSize: 13 }}>{error}</Box>}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={!canSubmit} onClick={() => mutation.mutate()}>
          {mutation.isPending ? 'Creating…' : 'Create activity'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
